"use client";

import { useCallback, useEffect, useState } from "react";

import {
  isEligibleForCareGachaAnnouncement,
  type ViewerForCareGachaAnnouncement,
} from "./careGachaAnnouncement";

const STORAGE_KEY_VERSION = "v1";

/** localStorage へ書けない端末でも、同一セッション中は再表示しないためのフォールバック */
const dismissedInSession = new Set<string>();

export const buildCareGachaAnnouncementDialogStorageKey = (
  workerId: string,
): string => `${STORAGE_KEY_VERSION}:careGachaAnnouncementDialog:${workerId}`;

type ViewerForDialog = ViewerForCareGachaAnnouncement & {
  id: string;
};

/**
 * ケアガチャ事前告知ダイアログの表示可否。
 * 1 ワーカー・1 端末につき 1 回だけ表示し、閉じた時点で表示済みとして記録する。
 * 記録に失敗した場合はそのセッション中だけ閉じた扱いとし、次回ログイン時は再表示され得る。
 */
export function useCareGachaAnnouncementDialog(
  viewer: ViewerForDialog | null | undefined,
) {
  const [isSeen, setIsSeen] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  const workerId = viewer?.id;

  useEffect(() => {
    if (!workerId) {
      setIsLoaded(false);
      return;
    }

    try {
      const stored = localStorage.getItem(
        buildCareGachaAnnouncementDialogStorageKey(workerId),
      );
      setIsSeen(stored === "true" || dismissedInSession.has(workerId));
    } catch (error) {
      console.warn("Failed to read from localStorage:", error);
      setIsSeen(dismissedInSession.has(workerId));
    } finally {
      setIsLoaded(true);
    }
  }, [workerId]);

  const markAsSeen = useCallback(() => {
    if (!workerId) return;

    dismissedInSession.add(workerId);
    setIsSeen(true);

    try {
      localStorage.setItem(
        buildCareGachaAnnouncementDialogStorageKey(workerId),
        "true",
      );
    } catch (error) {
      console.warn("Failed to write to localStorage:", error);
    }
  }, [workerId]);

  const shouldShowDialog = useCallback(() => {
    if (!isLoaded || !viewer) return false;
    if (isSeen) return false;
    return isEligibleForCareGachaAnnouncement(viewer);
  }, [isLoaded, isSeen, viewer]);

  return {
    shouldShowDialog,
    markAsSeen,
    isLoaded: Boolean(workerId) && isLoaded,
  };
}
